import type { GameplayConfig } from "../config/gameplay-config";

export type CombatantKind = "bot" | "player";

export interface SizeCombatant {
  readonly directionRadians: number;
  readonly id: string;
  readonly kind: CombatantKind;
  readonly level: number;
  readonly position: CombatPosition;
  readonly radius: number;
}

export interface CombatPosition {
  readonly x: number;
  readonly y: number;
}

export interface HostileContact {
  readonly firstId: string;
  readonly id: string;
  readonly observedAtMs: number;
  readonly runId: number;
  readonly secondId: string;
}

export type IgnoredCombatReason =
  | "duplicate-contact"
  | "invalid-contact"
  | "not-contacting"
  | "self-contact"
  | "stale-contact";

export interface IgnoredCombatResolution {
  readonly contactId: string;
  readonly kind: "ignored";
  readonly reason: IgnoredCombatReason;
}

export interface DefeatCombatResolution {
  readonly contactId: string;
  readonly defeatedId: string;
  readonly kind: "defeat";
  readonly observedAtMs: number;
  readonly runId: number;
  readonly winnerId: string;
}

export interface SeparateCombatResolution {
  readonly contactId: string;
  readonly first: {
    readonly directionRadians: number;
    readonly id: string;
    readonly position: CombatPosition;
  };
  readonly kind: "separate";
  readonly observedAtMs: number;
  readonly runId: number;
  readonly second: {
    readonly directionRadians: number;
    readonly id: string;
    readonly position: CombatPosition;
  };
}

export type AcceptedCombatResolution =
  DefeatCombatResolution | SeparateCombatResolution;

export type SizeCombatResolution =
  AcceptedCombatResolution | IgnoredCombatResolution;

export interface SizeCombatInput {
  readonly activeRunId: number;
  readonly combatants: readonly SizeCombatant[];
  readonly contact: HostileContact;
  readonly currentSimulationTimeMs: number;
  readonly processedContactIds: ReadonlySet<string>;
}

type CombatConfig = GameplayConfig["combat"];

const ignored = (
  contactId: string,
  reason: IgnoredCombatReason,
): IgnoredCombatResolution => ({ contactId, kind: "ignored", reason });

const isFinitePosition = (position: CombatPosition): boolean =>
  Number.isFinite(position.x) && Number.isFinite(position.y);

const isValidCombatant = (combatant: SizeCombatant): boolean =>
  combatant.id.trim().length > 0 &&
  Number.isFinite(combatant.directionRadians) &&
  Number.isSafeInteger(combatant.level) &&
  combatant.level > 0 &&
  Number.isFinite(combatant.radius) &&
  combatant.radius > 0 &&
  isFinitePosition(combatant.position);

const distanceBetween = (
  first: CombatPosition,
  second: CombatPosition,
): number => Math.hypot(second.x - first.x, second.y - first.y);

export const areCombatantsContacting = (
  first: SizeCombatant,
  second: SizeCombatant,
  config: CombatConfig,
): boolean =>
  distanceBetween(first.position, second.position) <=
  first.radius + second.radius + config.contactDistancePadding;

const separateCombatants = (
  first: SizeCombatant,
  second: SizeCombatant,
  config: CombatConfig,
): Pick<SeparateCombatResolution, "first" | "second"> => {
  const distance = distanceBetween(first.position, second.position);
  const direction =
    distance === 0
      ? { x: 1, y: 0 }
      : {
          x: (second.position.x - first.position.x) / distance,
          y: (second.position.y - first.position.y) / distance,
        };
  const targetDistance =
    first.radius + second.radius + config.separationDistance;
  const correction = Math.max(0, targetDistance - distance) / 2;
  const firstDirectionRadians = Math.atan2(-direction.y, -direction.x);
  const secondDirectionRadians = Math.atan2(direction.y, direction.x);
  return {
    first: {
      directionRadians: firstDirectionRadians,
      id: first.id,
      position: {
        x: first.position.x - direction.x * correction,
        y: first.position.y - direction.y * correction,
      },
    },
    second: {
      directionRadians: secondDirectionRadians,
      id: second.id,
      position: {
        x: second.position.x + direction.x * correction,
        y: second.position.y + direction.y * correction,
      },
    },
  };
};

const canonicalCombatants = (
  first: SizeCombatant,
  second: SizeCombatant,
): readonly [SizeCombatant, SizeCombatant] =>
  first.id < second.id ? [first, second] : [second, first];

export const resolveSizeCombatContact = (
  input: SizeCombatInput,
  config: CombatConfig,
): SizeCombatResolution => {
  const { contact } = input;
  if (
    contact.id.trim().length === 0 ||
    !Number.isSafeInteger(contact.runId) ||
    contact.runId <= 0 ||
    !Number.isFinite(contact.observedAtMs) ||
    contact.observedAtMs < 0 ||
    !Number.isSafeInteger(input.activeRunId) ||
    input.activeRunId <= 0 ||
    !Number.isFinite(input.currentSimulationTimeMs) ||
    input.currentSimulationTimeMs < 0
  ) {
    return ignored(contact.id, "invalid-contact");
  }
  if (contact.firstId === contact.secondId) {
    return ignored(contact.id, "self-contact");
  }
  if (input.processedContactIds.has(contact.id)) {
    return ignored(contact.id, "duplicate-contact");
  }
  const age = input.currentSimulationTimeMs - contact.observedAtMs;
  if (
    contact.runId !== input.activeRunId ||
    age < 0 ||
    age > config.contactMaximumAgeMs
  ) {
    return ignored(contact.id, "stale-contact");
  }
  const firstMatches = input.combatants.filter(
    (combatant) => combatant.id === contact.firstId,
  );
  const secondMatches = input.combatants.filter(
    (combatant) => combatant.id === contact.secondId,
  );
  if (firstMatches.length === 0 || secondMatches.length === 0) {
    return ignored(contact.id, "stale-contact");
  }
  const first = firstMatches[0];
  const second = secondMatches[0];
  if (
    first === undefined ||
    second === undefined ||
    firstMatches.length !== 1 ||
    secondMatches.length !== 1 ||
    !isValidCombatant(first) ||
    !isValidCombatant(second) ||
    (first.kind === "player" && second.kind === "player")
  ) {
    return ignored(contact.id, "invalid-contact");
  }
  if (!areCombatantsContacting(first, second, config)) {
    return ignored(contact.id, "not-contacting");
  }
  const levelDifference = first.level - second.level;
  if (Math.abs(levelDifference) >= config.defeatLevelGap) {
    return {
      contactId: contact.id,
      defeatedId: levelDifference > 0 ? second.id : first.id,
      kind: "defeat",
      observedAtMs: contact.observedAtMs,
      runId: contact.runId,
      winnerId: levelDifference > 0 ? first.id : second.id,
    };
  }
  const [canonicalFirst, canonicalSecond] = canonicalCombatants(first, second);
  return {
    contactId: contact.id,
    kind: "separate",
    observedAtMs: contact.observedAtMs,
    runId: contact.runId,
    ...separateCombatants(canonicalFirst, canonicalSecond, config),
  };
};
